import { useMutation } from '@tanstack/react-query';
import { HandCoins } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { Tip } from '@/components/shared/tip';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { isElevationCancelled, useElevateSession } from '@/hooks/use-elevate-session';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { type PaymentProvider, translatePaymentProviderKey } from '@/lib/payment-providers';
import { formatDateTime } from '@/utils/format';

interface PaymentConnectionSummary {
  id: string;
  provider: string;
  status: string;
  maskedHint: string | null;
  lastValidatedAt: string | null;
  lastValidationError: string | null;
}

const statusBadgeVariant = (status: string): BadgeVariant => {
  if (status === 'active') return 'success';
  if (status === 'invalid') return 'warning';
  if (status === 'disabled') return 'danger';
  return 'neutral';
};

const providerLabelKey = (key: string) => `provider.${key}` as const;
const providerSubtitleKey = (key: string) => `provider.subtitle.${key}` as const;

export const PaymentProviderConnectionCard = ({
  provider,
  connection,
  onUpdated,
}: {
  provider: PaymentProvider;
  connection: PaymentConnectionSummary | null;
  onUpdated: () => void;
}) => {
  const { t } = useTranslation('payments');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const { elevateAndRetry, requireElevationThen, dialog: sessionDialogs } = useElevateSession();

  const [connectOpen, setConnectOpen] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [credentials, setCredentials] = useState<Record<string, string>>({});

  const status = connection?.status ?? 'not_connected';
  const configured = Boolean(connection);
  const canPause = Boolean(connection) && (status === 'active' || status === 'invalid');
  const canResume = Boolean(connection) && status === 'disabled';
  const isHubtel = provider.key === 'hubtel';

  const requiredFields = provider.credentialFields.filter((field) => field.required !== false);
  const optionalFields = provider.credentialFields.filter((field) => field.required === false);
  const canSave = requiredFields.every((field) => credentials[field.key]?.trim());

  const providerName = t(providerLabelKey(provider.key), {
    defaultValue: provider.displayName,
  });
  const providerSubtitle = t(providerSubtitleKey(provider.key), {
    defaultValue: t('onlineGiving.defaultProviderSubtitle'),
  });

  const statusLabel =
    status === 'not_connected'
      ? t('onlineGiving.notConnected')
      : t(`connection.status.${status}`);

  const connectLabel = configured
    ? t('onlineGiving.updateProvider', { provider: providerName })
    : t('onlineGiving.connectProvider', { provider: providerName });

  const save = useMutation({
    mutationFn: () =>
      elevateAndRetry(
        () =>
          api.post('/payments/connections', {
            provider: provider.key,
            environment: 'live',
            credentials: Object.fromEntries(
              provider.credentialFields.map((field) => [
                field.key,
                credentials[field.key]?.trim() ?? '',
              ]),
            ),
          }),
        { description: t('onlineGiving.elevateDescription', { provider: providerName }) },
      ),
    onSuccess: () => {
      toast.success(t('onlineGiving.savedProvider', { provider: providerName }));
      setConnectOpen(false);
      setCredentials({});
      onUpdated();
    },
    onError: (err) => {
      if (isElevationCancelled(err)) return;
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const pause = useMutation({
    mutationFn: (connectionId: string) =>
      elevateAndRetry(
        () => api.post(`/payments/connections/${connectionId}/disable`, {}),
        { description: t('onlineGiving.pauseElevateDescription') },
      ),
    onSuccess: () => {
      toast.success(t('onlineGiving.paused'));
      setPauseOpen(false);
      onUpdated();
    },
    onError: (err) => {
      if (isElevationCancelled(err)) return;
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const resume = useMutation({
    mutationFn: (connectionId: string) =>
      elevateAndRetry(
        () => api.post(`/payments/connections/${connectionId}/enable`, {}),
        { description: t('onlineGiving.resumeElevateDescription') },
      ),
    onSuccess: () => {
      toast.success(t('onlineGiving.resumed'));
      onUpdated();
    },
    onError: (err) => {
      if (isElevationCancelled(err)) return;
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const openConnect = () => {
    requireElevationThen(() => {
      setCredentials({});
      setConnectOpen(true);
    }, t('onlineGiving.elevateDescription', { provider: providerName }));
  };

  const onboardingTips = useMemo(
    () =>
      provider.onboardingRequirements.map((requirement) => {
        if (requirement.kind !== 'ip_allowlist') return null;
        return (
          <div
            key={requirement.titleKey}
            className="rounded-md border border-border bg-surface-secondary p-3 text-body-sm text-fg-secondary"
          >
            <p className="font-medium text-fg">
              {translatePaymentProviderKey(requirement.titleKey, t)}
            </p>
            <p className="mt-1">{translatePaymentProviderKey(requirement.bodyKey, t)}</p>
          </div>
        );
      }),
    [provider.onboardingRequirements, t],
  );

  const hubtelGuidelines = isHubtel
    ? (t('onlineGiving.hubtelGuidelines.items', { returnObjects: true }) as string[])
    : null;

  return (
    <>
      <Card className="flex flex-col">
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <div
                className="flex size-10 shrink-0 items-center justify-center rounded-md bg-brand/10"
                aria-hidden
              >
                <HandCoins className="size-5 text-brand" />
              </div>
              <div className="min-w-0 space-y-1">
                <CardTitle className="text-heading-sm">{providerName}</CardTitle>
                <CardDescription>{providerSubtitle}</CardDescription>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              {provider.supportedMethods.map((method) => (
                <Badge key={method} variant="neutral">
                  {t(`onlineGiving.methods.${method}`, { defaultValue: method })}
                </Badge>
              ))}
              {provider.supportedCurrencies.map((currency) => (
                <Badge key={currency} variant="neutral">
                  {currency}
                </Badge>
              ))}
              <Badge variant={statusBadgeVariant(status)} className="shrink-0">
                {statusLabel}
              </Badge>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 space-y-4">
          {status === 'invalid' && connection?.lastValidationError ? (
            <p className="rounded-md border border-danger/20 bg-danger/5 px-3 py-2 text-body-sm text-danger">
              {tErrors(connection.lastValidationError)}
            </p>
          ) : null}

          {configured ? (
            <dl className="space-y-3">
              {connection?.maskedHint ? (
                <div className="grid gap-1 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-4">
                  <dt className="text-body-sm text-fg-secondary">
                    {t('onlineGiving.keysOnFile', { provider: providerName })}
                  </dt>
                  <dd className="font-mono text-body-sm text-fg">{connection.maskedHint}</dd>
                </div>
              ) : null}
              {connection?.lastValidatedAt ? (
                <div className="grid gap-1 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-4">
                  <dt className="text-body-sm text-fg-secondary">{t('connection.statusLabel')}</dt>
                  <dd className="text-body-sm text-fg">
                    {t('connection.lastValidated', {
                      date: formatDateTime(connection.lastValidatedAt),
                    })}
                  </dd>
                </div>
              ) : null}
            </dl>
          ) : (
            <p className="text-body-sm text-fg-secondary">
              {t('onlineGiving.notConnectedHelp', { provider: providerName })}
            </p>
          )}

          {connection?.maskedHint ? <Tip tipId="payments.maskedKey" mode="inline" /> : null}
        </CardContent>

        <CardFooter className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {configured ? (
              <Button asChild variant="ghost" size="sm" className="justify-start px-0 sm:px-3">
                <Link to="/payments">{t('onlineGiving.openPayments')}</Link>
              </Button>
            ) : null}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            {connection && canPause ? (
              <Button
                variant="secondary"
                disabled={pause.isPending || resume.isPending}
                onClick={() => {
                  requireElevationThen(
                    () => setPauseOpen(true),
                    t('onlineGiving.pauseElevateDescription'),
                  );
                }}
              >
                {t('onlineGiving.pause')}
              </Button>
            ) : null}
            {connection && canResume ? (
              <Button
                variant="secondary"
                disabled={pause.isPending || resume.isPending}
                onClick={() => resume.mutate(connection.id)}
              >
                {t('onlineGiving.resume')}
              </Button>
            ) : null}
            <Button onClick={openConnect}>{connectLabel}</Button>
          </div>
        </CardFooter>
      </Card>

      <FormDialog
        open={connectOpen}
        onOpenChange={setConnectOpen}
        title={connectLabel}
        description={t('onlineGiving.connectProviderDescription', { provider: providerName })}
        size="md"
        closeLabel={tCommon('action.close')}
        cancelLabel={tCommon('action.cancel')}
        submitLabel={configured ? t('onlineGiving.save') : connectLabel}
        submitting={save.isPending}
        submitDisabled={!canSave}
        onSubmit={async () => {
          await save.mutateAsync();
          return;
        }}
      >
        <div className="space-y-4">
          <Tip tipId="payments.elevatedSession" />
          <Tip tipId="payments.testVsLive" />
          {onboardingTips}
          {hubtelGuidelines ? (
            <div className="rounded-md border border-border bg-surface-secondary p-3 space-y-1">
              <p className="text-caption font-medium text-fg">
                {t('onlineGiving.hubtelGuidelines.title')}
              </p>
              <ul className="list-disc space-y-1 pl-4 text-caption text-fg-secondary">
                {hubtelGuidelines.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {requiredFields.map((field) => (
            <FormField
              key={field.key}
              label={translatePaymentProviderKey(field.labelKey, t)}
              hint={field.helpKey ? translatePaymentProviderKey(field.helpKey, t) : undefined}
              tipId={field.secret ? 'payments.credentialsWriteOnly' : undefined}
              popoverPortal="dialog"
            >
              {({ id }) => (
                <Input
                  id={id}
                  type={field.secret ? 'password' : 'text'}
                  value={credentials[field.key] ?? ''}
                  onChange={(event) =>
                    setCredentials((current) => ({
                      ...current,
                      [field.key]: event.target.value,
                    }))
                  }
                  autoComplete={field.secret ? 'new-password' : 'off'}
                />
              )}
            </FormField>
          ))}
          {optionalFields.map((field) => (
            <FormField
              key={field.key}
              label={translatePaymentProviderKey(field.labelKey, t)}
              hint={field.helpKey ? translatePaymentProviderKey(field.helpKey, t) : undefined}
              popoverPortal="dialog"
            >
              {({ id }) => (
                <Input
                  id={id}
                  value={credentials[field.key] ?? ''}
                  onChange={(event) =>
                    setCredentials((current) => ({
                      ...current,
                      [field.key]: event.target.value,
                    }))
                  }
                  autoComplete="off"
                />
              )}
            </FormField>
          ))}
        </div>
      </FormDialog>

      <ConfirmDialog
        open={pauseOpen}
        onOpenChange={setPauseOpen}
        title={t('onlineGiving.pauseConfirmTitle')}
        description={t('onlineGiving.pauseConfirmDescription')}
        confirmLabel={t('onlineGiving.pauseConfirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={pause.isPending}
        onConfirm={async () => {
          if (!connection) return;
          await pause.mutateAsync(connection.id);
        }}
      />

      {sessionDialogs}
    </>
  );
};
